import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Eye, MessageCircle } from "lucide-react";
import { useProfile } from "@/context/ProfileContext";
import { PageLoader } from "@/components/auth/Guards";
import { VideoPlayer } from "@/components/video/VideoPlayer";
import { ShareButton } from "@/components/video/ShareButton";
import { CreatorChip } from "@/components/video/CreatorChip";
import { LikeButton } from "@/components/music/LikeButton";
import { Comments } from "@/components/music/Comments";
import { formatCount, timeAgo } from "@/lib/format";

export default function WatchPage() {
  const { id } = useParams();
  const { request } = useProfile();
  const [video, setVideo] = useState(undefined);

  useEffect(() => { setVideo(undefined); request("get", `/videos/${id}`).then(setVideo).catch(() => setVideo(null)); }, [id, request]);

  if (video === undefined) return <PageLoader />;
  if (video === null) return (
    <div data-testid="video-not-found" className="py-24 text-center space-y-4">
      <h1 className="font-display text-4xl font-extrabold chrome-text">Video not found</h1>
      <Link to="/videos" className="btn-red inline-block px-5 py-2.5 text-sm">Back to videos</Link>
    </div>
  );

  return (
    <div data-testid="watch-page" className="space-y-6 animate-rise">
      <VideoPlayer key={video.id} video={video} onViewed={() => setVideo((v) => ({ ...v, view_count: v.view_count + 1 }))} />
      <div className="space-y-4">
        <div className="space-y-1">
          <span className="rounded-full border border-white/15 px-2.5 py-0.5 text-[10px] uppercase tracking-widest text-slate-300">{video.category}</span>
          <h1 data-testid="watch-title" className="pt-2 font-display text-2xl sm:text-4xl font-extrabold tracking-tight text-white break-words">{video.title}</h1>
          <p className="flex items-center gap-3 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1"><Eye className="h-3.5 w-3.5" /><span data-testid="watch-view-count">{formatCount(video.view_count)}</span> {video.view_count === 1 ? "view" : "views"}</span>
            <span>{timeAgo(video.created_at)}</span>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {video.owner && <CreatorChip owner={video.owner} />}
          <div className="flex items-center gap-2 sm:ml-auto">
            <LikeButton track={video} kind="videos" size="lg" />
            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-sm text-slate-400"><MessageCircle className="h-5 w-5" /><span data-testid="watch-comment-count">{formatCount(video.comment_count)}</span></span>
            <ShareButton path={`/watch/${video.id}`} title={video.title} testId="watch-share-button" className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-sm text-slate-300 hover:text-white" />
          </div>
        </div>
        {video.description && <p data-testid="watch-description" className="max-w-3xl rounded-2xl border border-white/10 bg-nw-surface/60 p-4 text-sm leading-relaxed text-slate-300 whitespace-pre-line">{video.description}</p>}
      </div>
      <Comments trackId={video.id} kind="videos" onCountChange={(d) => setVideo((v) => ({ ...v, comment_count: v.comment_count + d }))} />
    </div>
  );
}
